import { ApiResponse } from '@/utils';
import { enhancedRequest } from '@/utils/enhancedRequest';
import { handleSmsError } from '@/utils/errorHandler';
import { SendSmsCodeParams, SmsCodeType } from '../types';

/**
 * AMAI短信服务
 * 用于处理短信验证码相关功能
 */
class SmsService {
  /**
   * 发送短信验证码
   * @param params 请求参数 {mobile: string, country_code?: string, type: number}
   * @returns 基础响应
   */
  async sendSmsCode(params: SendSmsCodeParams): Promise<ApiResponse<any>> {
    try {
      // 设置默认国家区号
      const requestParams = {
        country_code: '86',
        ...params,
      };

      // 验证参数
      if (!requestParams.mobile) {
        throw new Error('手机号不能为空');
      }

      if (![SmsCodeType.UPDATE_PHONE, SmsCodeType.LOGIN, 3].includes(requestParams.type)) {
        throw new Error('短信验证码类型无效');
      }

      return await enhancedRequest.post<any>('/send-sms-code', requestParams, {
        enableDeduplication: true,
        requestKey: `send-sms-code:${requestParams.mobile}:${requestParams.type}`,
        retryCount: 1,
        timeout: 15000, // 短信发送15秒超时
      });
    } catch (error) {
      // 使用专门的短信错误处理函数
      const errorInfo = handleSmsError(error, '发送短信验证码');
      throw new Error(errorInfo.message);
    }
  }

  /**
   * 发送更新手机号验证码
   * @param mobile 手机号
   * @param countryCode 国家区号，默认86
   * @param type 验证码类型，默认1（更新手机号），入驻和认证时传3
   * @returns 基础响应
   */
  async sendUpdatePhoneCode(mobile: string, countryCode: string = '86', type: number = SmsCodeType.UPDATE_PHONE): Promise<ApiResponse<any>> {
    return this.sendSmsCode({
      mobile,
      country_code: countryCode,
      type,
    });
  }

  /**
   * 发送登录验证码
   * @param mobile 手机号
   * @param countryCode 国家区号，默认86
   * @returns 基础响应
   */
  async sendLoginCode(mobile: string, countryCode: string = '86'): Promise<ApiResponse<any>> {
    return this.sendSmsCode({
      mobile,
      country_code: countryCode,
      type: SmsCodeType.LOGIN,
    });
  }
}

export default new SmsService();
